import { HFCL_AGENT_URL } from '../../components/aiChatModule/hfclChatConfig';
import {canvasPermissionHeader} from './agentPermissions';
import { getUserInfo } from '../../utils/tokenUtils';
import { getAgentPageContext } from './contextRegistry';
import { beginTurn, countTurnCall, markTurn } from './turnTiming';
import {
	discoverResourcePlugins,
	isCapabilitySupported,
	serializeResourcePageContext,
} from './resourcePluginRegistry';

let backendCapabilityIds = null;
let backendCapabilitiesPromise = null;

/**
 * Headers every agent API call carries.
 *
 * The Canvas token is what the agent backend verifies to learn who the user
 * is. Without it the backend can only fall back to a single shared dev
 * identity, which puts every user's history and sessions in one bucket.
 *
 * @param {object} [extra] additional headers
 * @return {object} request headers
 */
export function agentHeaders(extra = {}) {
	const headers = {...extra};
	let token = null;
	try {
		token = getUserInfo();
	} catch (_) {
		token = null;
	}
	if (token && token !== 'undefined' && token !== 'null') {
		headers.Authorization = token;
	}
	// What this user may do, from the permissions Canvas already put in
	// localStorage. A header rather than a body field because capability
	// discovery is a GET and has no body, and both calls need it: a
	// capability filtered out of discovery but allowed on chat, or the
	// reverse, is worse than either answer on its own.
	//
	// Omitted entirely when there is nothing to send. The backend reads a
	// missing header as "no claim" and keeps its own list, so a user whose
	// storage has not loaded yet is not refused everything.
	try {
		const permissions = canvasPermissionHeader();
		if (permissions) {
			headers['X-Canvas-Permissions'] = permissions;
		}
	} catch (_) {
		// Never block a request over this: without the header the backend
		// behaves exactly as it did before.
	}
	return headers;
}

/**
 * How long capability discovery may take before it stops being waited on.
 *
 * It exists to hide a suggestion this client cannot carry out. That is worth
 * a moment and not worth a turn: an empty set filters nothing, which is the
 * behaviour a failed discovery already had.
 */
const DISCOVERY_DEADLINE_MS = 4000;

/**
 * How long one chat turn may take before the client stops waiting.
 *
 * Generous, because the model is the slow part and a correct answer late
 * beats a timeout: measured on this deployment a cold turn runs to about a
 * minute. What this stops is the case with no upper bound at all - a request
 * that never settles held the spinner and the session's turn open for the
 * life of the page, and nothing could cancel it.
 *
 * Configurable, because the same client runs against deployments whose model
 * is nowhere near as slow.
 */
export const CHAT_DEADLINE_MS = Number(
	process.env.REACT_APP_HFCL_AGENT_CHAT_TIMEOUT_MS,
) || 180000;

/**
 * A chat turn that did not finish in time, as a type rather than a string.
 *
 * The UI distinguishes "we gave up waiting" from "the server said no", and
 * matching on message text is how that distinction rots.
 */
export class AgentChatTimeout extends Error {
	/**
	 * @param {number} ms deadline that elapsed
	 */
	constructor(ms) {
		super(`Agent chat exceeded ${ms}ms`);
		this.name = 'AgentChatTimeout';
		this.timeout = true;
		this.timeoutMs = ms;
	}
}

/**
 * A chat turn cancelled by the caller - a new chat, a reset, an unmount.
 *
 * Distinct from a timeout because nothing went wrong: the answer stopped
 * being wanted. The UI shows nothing for this.
 */
export class AgentChatCancelled extends Error {
	/** Built with no detail: the caller already knows why it cancelled. */
	constructor() {
		super('Agent chat cancelled');
		this.name = 'AgentChatCancelled';
		this.cancelled = true;
	}
}

/**
 * The set this request will use when discovery is too slow to wait for.
 *
 * Empty, so nothing is filtered. Showing a suggestion this client might not
 * support is a small wrong; holding an answer the user already has is not.
 */
const NOTHING_KNOWN = new Set();

/**
 * Load and cache backend capability identifiers.
 *
 * Bounded twice, because the two limits stop different things. The abort
 * below stops the request itself from sitting open on a stalled connection.
 * The deadline at the call site stops a completed chat reply from waiting on
 * it - a promise that never settles is not fixed by cancelling the fetch
 * behind it, and this one was awaited with no way out.
 *
 * @return {Promise<Set<string>>} capability identifiers
 */
async function loadBackendCapabilityIds() {
	if (backendCapabilityIds) return backendCapabilityIds;
	if (!backendCapabilitiesPromise) {
		const controller = typeof AbortController === 'function'
			? new AbortController()
			: null;
		const giveUp = setTimeout(() => controller?.abort(), DISCOVERY_DEADLINE_MS);
		backendCapabilitiesPromise = fetch(`${HFCL_AGENT_URL}api/agent/capabilities`, {
			method: 'GET',
			credentials: 'include',
			headers: agentHeaders(),
			signal: controller?.signal,
		})
			.then((response) => {
				if (!response.ok) throw new Error('capability_discovery_failed');
				return response.json();
			})
			.then((document) => {
				backendCapabilityIds = new Set(
					(document.capabilities || []).map((item) => item.capability_id),
				);
				return backendCapabilityIds;
			})
			.catch(() => {
				// Cleared so the next turn tries again rather than inheriting
				// one bad moment for the life of the page.
				backendCapabilitiesPromise = null;
				return NOTHING_KNOWN;
			})
			.finally(() => clearTimeout(giveUp));
	}
	return backendCapabilitiesPromise;
}

/**
 * The capability set, or nothing, whichever arrives first.
 *
 * The discovery keeps running either way: it is cached, so a turn that gave
 * up on it still leaves the answer in place for the next one.
 *
 * @param {Promise<Set<string>>} pending discovery in flight
 * @return {Promise<Set<string>>} what is known in time
 */
function knownInTime(pending) {
	return new Promise((resolve) => {
		const late = setTimeout(() => resolve(NOTHING_KNOWN), DISCOVERY_DEADLINE_MS);
		pending
			.then((known) => {
				clearTimeout(late);
				resolve(known || NOTHING_KNOWN);
			})
			.catch(() => {
				clearTimeout(late);
				resolve(NOTHING_KNOWN);
			});
	});
}

/**
 * Remove backend capability suggestions unsupported by this client.
 * @param {object} response agent response
 * @param {Set<string>} knownCapabilities backend capability identifiers
 * @return {object} compatible response
 */
function filterUnsupportedSuggestions(response, knownCapabilities) {
	if (!Array.isArray(response?.suggestions) || !knownCapabilities.size) {
		return response;
	}
	return {
		...response,
		suggestions: response.suggestions.filter((suggestion) => (
			!knownCapabilities.has(suggestion.id) ||
			isCapabilitySupported(suggestion.id)
		)),
	};
}

/**
 * Describe where the user actually is, in the agent's strict public contract.
 *
 * Read only from the mounted-page registry. The legacy chat store used to win
 * here, and it is a window global that pages update by hand and nothing clears
 * on unmount - so after moving from a Site to an Access Point it still claimed
 * the Site route, and the AP the user was looking at was reported as null.
 * Identity and arbitrary page data must never be forwarded.
 *
 * @return {object} strict public page context
 */
export function normalizeAgentPageContext() {
	const page = getAgentPageContext();
	const section = page.section;
	return {
		route: page.route,
		section: typeof section === 'string' && section ? section : null,
		...serializeResourcePageContext(page),
	};
}

/**
 * The backend's request id out of an error body, when there is one.
 *
 * Support-safe on purpose: an id and nothing else. The rest of the detail
 * can name a code path and belongs in diagnostics, not on screen.
 *
 * @param {string} detail response body
 * @return {string|null} request id
 */
function requestIdFrom(detail) {
	try {
		const parsed = JSON.parse(detail);
		const id = parsed?.detail?.request_id || parsed?.request_id;
		return typeof id === 'string' && id ? id : null;
	} catch {
		return null;
	}
}

/**
 * Send one authenticated message to the independent agent API.
 * @param {string} message user message
 * @param {object} options request context
 * @return {Promise<object>} agent response
 */
export async function sendAgentMessage(message, options = {}) {
	const knownCapabilitiesPromise = loadBackendCapabilityIds();
	// The turn starts here, not when the backend hears about it: the time
	// spent getting there is time the user waited.
	const turnId = beginTurn();
	countTurnCall(turnId);
	// Two ways this request can stop being waited on, joined into one signal.
	//
	// The deadline is ours; `options.signal` is the caller's - a new chat, a
	// reset, an unmount. Neither existed before: the chat fetch had no
	// AbortSignal at all, so a request that never settled held the spinner
	// and the turn open for the life of the page.
	//
	// Aborting is not proof the server stopped. It stops *us* waiting, and
	// the guards downstream are what make a late reply harmless.
	const deadline = typeof AbortController === 'function'
		? new AbortController()
		: null;
	const expired = setTimeout(() => deadline?.abort(), CHAT_DEADLINE_MS);
	const giveUp = () => deadline?.abort();
	// A signal that has already fired never fires again, so a caller who
	// cancelled before we got here would otherwise be ignored entirely.
	if (options.signal?.aborted) giveUp();
	options.signal?.addEventListener?.('abort', giveUp);
	/**
	 * Why this turn stopped, as a type rather than as a guess.
	 * @param {Error} failure the abort that was raised
	 * @return {Error} the typed failure to surface
	 */
	const whyItStopped = (failure) => {
		if (failure?.name !== 'AbortError') return failure;
		return options.signal?.aborted
			? new AgentChatCancelled()
			: new AgentChatTimeout(CHAT_DEADLINE_MS);
	};
	/**
	 * A promise that rejects the moment this turn stops being waited on.
	 *
	 * Raced against reading the body rather than trusting the body to honour
	 * the signal. `fetch` aborts a stalled *network* read, but a body that
	 * resolves from somewhere else - a polyfill, a service worker, a test
	 * double - never sees the abort at all, and the turn then hangs with the
	 * signal already fired. Racing settles it either way.
	 *
	 * @return {Promise<never>} a promise that only ever rejects
	 */
	const stopsWaiting = () => new Promise((_resolve, reject) => {
		const stop = () => {
			const aborted = new Error('aborted');
			aborted.name = 'AbortError';
			reject(aborted);
		};
		if (deadline?.signal.aborted) stop();
		else deadline?.signal.addEventListener('abort', stop, {once: true});
	});
	/** Released on every path, once the result is actually in hand. */
	const release = () => {
		clearTimeout(expired);
		options.signal?.removeEventListener?.('abort', giveUp);
	};
	let response;
	try {
		response = await fetch(`${HFCL_AGENT_URL}api/agent/chat`, {
			method: 'POST',
			credentials: 'include',
			headers: agentHeaders({'Content-Type': 'application/json'}),
			signal: deadline?.signal,
			body: JSON.stringify({
				message,
				session_id: options.sessionId,
				turn_id: turnId,
				page_context: normalizeAgentPageContext(),
				selected_capability_id: options.selectedCapabilityId || null,
				selected_choice_id: options.selectedChoiceId || null,
			}),
		});
	} catch (failure) {
		markTurn(turnId, 'failed_ms');
		release();
		// Which of the two aborts fired decides what the user is told. A
		// cancellation is silent - they asked for it - and a timeout is not.
		throw whyItStopped(failure);
	}
	// Deliberately NOT released yet.
	//
	// `fetch` resolves when the response *headers* arrive; the body is still
	// streaming. Clearing the deadline here - which is what it used to do -
	// meant a server that sent headers and then stalled held the turn open
	// with nothing able to stop it: measured at 20ms deadline, still pending
	// and un-aborted 80ms later. The body is part of the turn, so the
	// deadline covers it.
	markTurn(turnId, 'reply_received_ms');
	if (!response.ok) {
		markTurn(turnId, 'failed_ms');
		let detail;
		try {
			detail = await Promise.race([response.text(), stopsWaiting()]);
		} catch (failure) {
			release();
			throw whyItStopped(failure);
		}
		// L06. The backend puts a request id in its 503 body. Turning the
		// whole thing into one string threw away the only handle support
		// has on the turn that failed, so the id is carried on the error
		// where the UI can show it and the rest stays where it was.
		release();
		const error = new Error(`Agent chat failed: ${response.status} ${detail}`);
		error.status = response.status;
		error.requestId = requestIdFrom(detail);
		throw error;
	}
	let document;
	let knownCapabilities;
	try {
		[document, knownCapabilities] = await Promise.all([
			Promise.race([response.json(), stopsWaiting()]),
		// Deadlined. The reply is already in hand at this point, so an
		// optional lookup that never settles was holding a finished answer
		// and the spinner over it for as long as it took - which, with no
		// timeout on that request, was forever.
			knownInTime(knownCapabilitiesPromise),
		]);
	} catch (failure) {
		// A body that stalls or is cut off aborts here, and it aborts for one
		// of the same two reasons. Normalised so the UI does not have to tell
		// a torn body from a refused request.
		markTurn(turnId, 'failed_ms');
		release();
		throw whyItStopped(failure);
	}
	// Held open until the result is genuinely in hand, and released exactly
	// once. Aborting stops us waiting; it is not a claim the server stopped.
	release();
	return filterUnsupportedSuggestions(document, knownCapabilities);
}

/**
 * List the signed-in user's chat conversations, newest first.
 * @param {object} options pagination
 * @param {number} [options.limit] page size
 * @param {string} [options.cursor] opaque cursor from a previous page's `next_cursor`
 * @return {Promise<{conversations: object[], next_cursor: (string|null)}>} page of conversations
 */
export async function fetchConversations({limit = 30, cursor} = {}) {
	const params = new URLSearchParams();
	params.set('limit', String(limit));
	if (cursor) params.set('cursor', cursor);
	const response = await fetch(
		`${HFCL_AGENT_URL}api/agent/conversations?${params.toString()}`,
		{method: 'GET', credentials: 'include', headers: agentHeaders()},
	);
	if (!response.ok) {
		throw new Error(`Fetch conversations failed: ${response.status}`);
	}
	return response.json();
}

/**
 * Fetch one conversation's full transcript by its session id.
 * @param {string} sessionId conversation id (the session id it was created under)
 * @return {Promise<object>} conversation with `messages`
 */
export async function fetchConversation(sessionId) {
	const response = await fetch(
		`${HFCL_AGENT_URL}api/agent/conversations/${encodeURIComponent(sessionId)}`,
		{method: 'GET', credentials: 'include', headers: agentHeaders()},
	);
	if (!response.ok) {
		throw new Error(`Fetch conversation failed: ${response.status}`);
	}
	return response.json();
}

/**
 * Rename a conversation.
 * @param {string} sessionId conversation id
 * @param {string} title new title
 * @return {Promise<object>} updated conversation summary
 */
export async function renameConversation(sessionId, title) {
	const response = await fetch(
		`${HFCL_AGENT_URL}api/agent/conversations/${encodeURIComponent(sessionId)}`,
		{
			method: 'PATCH',
			credentials: 'include',
			headers: agentHeaders({'Content-Type': 'application/json'}),
			body: JSON.stringify({title}),
		},
	);
	if (!response.ok) {
		throw new Error(`Rename conversation failed: ${response.status}`);
	}
	return response.json();
}

/**
 * Delete a conversation and its messages.
 * @param {string} sessionId conversation id
 * @return {Promise<void>} resolves once deleted (or already gone)
 */
export async function deleteConversation(sessionId) {
	const response = await fetch(
		`${HFCL_AGENT_URL}api/agent/conversations/${encodeURIComponent(sessionId)}`,
		{method: 'DELETE', credentials: 'include', headers: agentHeaders()},
	);
	if (!response.ok && response.status !== 404) {
		throw new Error(`Delete conversation failed: ${response.status}`);
	}
}

/**
 * Return frontend plugin discovery metadata.
 * @return {object} client plugin manifest
 */
export function getAgentPluginManifest() {
	return {
		command_version: 1,
		plugins: discoverResourcePlugins(),
	};
}
